<template>
  <div class="reservation-form">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10 col-xl-8">
          <div class="reservation-card">
            <div class="reservation-header">
              <h3 class="reservation-title">{{ $t('reservation.title') }}</h3>
            </div>
            <div class="reservation-body">
              <!-- Progress Steps -->
              <div class="progress-steps mb-4">
                <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                  <div class="step-number">1</div>
                  <div class="step-label">{{ $t('reservation.steps.details') }}</div>
                </div>
                <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                  <div class="step-number">2</div>
                  <div class="step-label">{{ $t('reservation.steps.people') }}</div>
                </div>
                <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
                  <div class="step-number">3</div>
                  <div class="step-label">{{ $t('reservation.steps.payment') }}</div>
                </div>
                <div class="step" :class="{ active: currentStep >= 4 }">
                  <div class="step-number">4</div>
                  <div class="step-label">{{ $t('reservation.steps.confirmation') }}</div>
                </div>
              </div>

              <!-- Step 1: Contact Details -->
              <div v-if="currentStep === 1" class="step-content">
                <h4 class="step-title">{{ $t('reservation.contactDetails') }}</h4>
                <div class="form-row-modern">
                  <div class="form-group-modern">
                    <label for="touristName" class="form-label-modern">
                      {{ $t('reservation.name') }} *
                    </label>
                    <input 
                      type="text" 
                      id="touristName" 
                      class="form-input-modern" 
                      v-model="reservation.touristName"
                      :class="{ 'input-error': errors.touristName }"
                    >
                    <div v-if="errors.touristName" class="error-message">{{ errors.touristName }}</div>
                  </div>
                  <div class="form-group-modern">
                    <label for="touristEmail" class="form-label-modern">
                      {{ $t('reservation.email') }} *
                    </label>
                    <input 
                      type="email" 
                      id="touristEmail" 
                      class="form-input-modern" 
                      v-model="reservation.touristEmail"
                      :class="{ 'input-error': errors.touristEmail }"
                    >
                    <div v-if="errors.touristEmail" class="error-message">{{ errors.touristEmail }}</div>
                  </div>
                  <div class="form-group-modern">
                    <label for="touristPhone" class="form-label-modern">
                      {{ $t('reservation.phone') }} *
                    </label>
                    <input 
                      type="tel" 
                      id="touristPhone" 
                      class="form-input-modern" 
                      v-model="reservation.touristPhone"
                      :class="{ 'input-error': errors.touristPhone }"
                    >
                    <div v-if="errors.touristPhone" class="error-message">{{ errors.touristPhone }}</div>
                  </div>
                  <div class="form-group-modern">
                    <label for="language" class="form-label-modern">
                      {{ $t('reservation.language') }} *
                    </label>
                    <select 
                      id="language" 
                      class="form-input-modern" 
                      v-model="reservation.language"
                      :class="{ 'input-error': errors.language }"
                    >
                      <option value="">{{ $t('reservation.selectLanguage') }}</option>
                      <option value="pt">Português</option>
                      <option value="en">English</option>
                    </select>
                    <div v-if="errors.language" class="error-message">{{ errors.language }}</div>
                  </div>
                  <div class="form-group-modern full-width">
                    <label for="restrictions" class="form-label-modern">
                      {{ $t('reservation.restrictions') }}
                    </label>
                    <textarea 
                      id="restrictions" 
                      class="form-input-modern form-textarea" 
                      rows="3" 
                      v-model="reservation.restrictions"
                      :placeholder="$t('reservation.restrictionsPlaceholder')"
                    ></textarea>
                  </div>
                  <div class="form-group-modern full-width">
                    <label for="notes" class="form-label-modern">
                      {{ $t('reservation.notes') }}
                    </label>
                    <textarea 
                      id="notes" 
                      class="form-input-modern form-textarea" 
                      rows="3" 
                      v-model="reservation.notes"
                      :placeholder="$t('reservation.notesPlaceholder')"
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- Step 2: People Details -->
              <div v-if="currentStep === 2" class="step-content">
                <h4 class="step-title">{{ $t('reservation.peopleDetails') }}</h4>
                <p class="step-subtitle">{{ $t('reservation.peopleSubtitle', { count: reservation.numberOfPeople }) }}</p>
                
                <div v-for="(person, index) in reservation.people" :key="index" class="person-card">
                  <div class="card">
                    <div class="card-header-modern">
                      <h5 class="card-title-modern">{{ $t('reservation.person') }} {{ index + 1 }}</h5>
                      <button 
                        v-if="index > 0" 
                        type="button" 
                        class="btn-remove-person"
                        @click="removePerson(index)"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </button>
                    </div>
                    <div class="card-body">
                      <div class="form-row-modern">
                        <div class="form-group-modern">
                          <label class="form-label-modern">
                            {{ $t('reservation.personName') }} *
                          </label>
                          <input 
                            type="text" 
                            class="form-input-modern" 
                            v-model="person.name"
                            :class="{ 'input-error': errors[`person_${index}_name`] }"
                          >
                          <div v-if="errors[`person_${index}_name`]" class="error-message">{{ errors[`person_${index}_name`] }}</div>
                        </div>
                        <div class="form-group-modern">
                          <label class="form-label-modern">
                            {{ $t('reservation.ageBracket') }} *
                          </label>
                          <select 
                            class="form-input-modern" 
                            v-model="person.ageBracket"
                            :class="{ 'input-error': errors[`person_${index}_ageBracket`] }"
                          >
                            <option value="">{{ $t('reservation.selectAgeBracket') }}</option>
                            <option value="ZeroToFive">{{ $t('reservation.ageBrackets.zerotofive') }}{{ bracketPriceSuffix('ZeroToFive') }}</option>
                            <option value="SixToEleven">{{ $t('reservation.ageBrackets.sixtoeleven') }}{{ bracketPriceSuffix('SixToEleven') }}</option>
                            <option value="TwelveToSeventeen">{{ $t('reservation.ageBrackets.twelvetoseventeen') }}{{ bracketPriceSuffix('TwelveToSeventeen') }}</option>
                            <option value="Adult">{{ $t('reservation.ageBrackets.adult') }}{{ bracketPriceSuffix('Adult') }}</option>
                            <option value="Senior">{{ $t('reservation.ageBrackets.senior') }}{{ bracketPriceSuffix('Senior') }}</option>
                          </select>
                          <div v-if="errors[`person_${index}_ageBracket`]" class="error-message">{{ errors[`person_${index}_ageBracket`] }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  v-if="reservation.people.length < maxAllowedPeople"
                  type="button"
                  class="btn-add-person"
                  @click="addPerson"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>{{ $t('reservation.addPerson') }}</span>
                </button>
              </div>

              <!-- Step 3: Payment -->
              <div v-if="currentStep === 3" class="step-content">
                <h4>{{ $t('reservation.payment') }}</h4>
                
                <div v-if="experience.needsPrePayment" class="payment-required">
                  <div class="alert alert-info">
                    <i class="fas fa-info-circle"></i>
                    {{ $t('reservation.prePaymentRequired') }}
                    <span v-if="prePaymentPercentage < 100"> ({{ prePaymentPercentage }}%)</span>
                  </div>

                  <!-- Payment Breakdown -->
                  <div class="payment-breakdown mb-4">
                    <div class="breakdown-item">
                      <span>{{ $t('reservation.totalPrice') }}:</span>
                      <span v-if="appliedVoucher" class="price-with-discount">
                        <span class="original-price">€{{ formatPrice(calculatedPrice) }}</span>
                        <strong class="discounted-price">€{{ formatPrice(finalPrice) }}</strong>
                      </span>
                      <strong v-else>€{{ formatPrice(finalPrice) }}</strong>
                    </div>
                    <div v-if="prePaymentPercentage < 100" class="breakdown-item highlight">
                      <span>{{ $t('reservation.payNow') }} ({{ prePaymentPercentage }}%):</span>
                      <strong class="text-primary">€{{ formatPrice(prePaymentAmount) }}</strong>
                    </div>
                    <div v-if="prePaymentPercentage < 100" class="breakdown-item">
                      <span>{{ $t('reservation.payOnArrival') }}:</span>
                      <strong>€{{ formatPrice(remainingAmount) }}</strong>
                    </div>
                  </div>
                  
                  <div class="payment-options">
                    <div class="form-check mb-3">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        id="bankTransfer" 
                        value="BankTransfer" 
                        v-model="reservation.paymentMethod"
                      >
                      <label class="form-check-label" for="bankTransfer">
                        <i class="fas fa-university"></i>
                        {{ $t('reservation.bankTransfer') }}
                      </label>
                    </div>
                    
                    <div v-if="cardEnabled" class="form-check mb-3">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        id="creditCard" 
                        value="CreditCard" 
                        v-model="reservation.paymentMethod"
                      >
                      <label class="form-check-label" for="creditCard">
                        <i class="fas fa-credit-card"></i>
                        {{ $t('reservation.creditCard') }}
                      </label>
                    </div>
                  </div>
                  
                  <div v-if="reservation.paymentMethod === 'BankTransfer'" class="bank-details mt-4">
                    <h5>{{ $t('reservation.bankDetails') }}</h5>
                    <div class="bank-info">
                      <p><strong>{{ $t('reservation.bankName') }}:</strong> Caixa Central Crédito Agrícola Mútuo</p>
                      <p><strong>{{ $t('reservation.beneficiaryName') }}:</strong> Quinta Do Barbusano</p>
                      <p><strong>{{ $t('reservation.iban') }}:</strong> PT50 0045 9090 4033 4811 5269 7</p>
                      <p><strong>{{ $t('reservation.swift') }}:</strong> CCCMPTPL</p>
                      <p class="final-amount">
                        <strong>{{ $t('reservation.amountToPay') }}:</strong>
                        <span v-if="appliedVoucher" class="price-with-discount">
                          <span class="original-price">€{{ formatPrice(calculatedPrice * prePaymentPercentage / 100) }}</span>
                          <span class="discounted-price">€{{ formatPrice(prePaymentAmount) }}</span>
                        </span>
                        <span v-else>€{{ formatPrice(prePaymentAmount) }}</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div v-else class="no-payment-required">
                  <div class="alert alert-success">
                    <i class="fas fa-check-circle"></i>
                    {{ $t('reservation.noPrePaymentRequired') }}
                  </div>
                </div>

                <!-- Voucher Code -->
                <div v-if="experience.needsPrePayment" class="voucher-section mt-4">
                  <h5>{{ $t('reservation.voucher') }}</h5>
                  <div class="row">
                    <div class="col-md-8">
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="voucherCode"
                        :placeholder="$t('reservation.voucherPlaceholder')"
                      >
                    </div>
                    <div class="col-md-4">
                      <button 
                        type="button" 
                        class="btn btn-outline-primary w-100"
                        @click="applyVoucher"
                        :disabled="!voucherCode || voucherLoading"
                      >
                        <span v-if="voucherLoading" class="spinner-border spinner-border-sm mr-2"></span>
                        {{ $t('reservation.applyVoucher') }}
                      </button>
                    </div>
                  </div>
                  <div v-if="voucherMessage" class="mt-2" :class="voucherMessage.type === 'success' ? 'text-success' : 'text-danger'">
                    {{ voucherMessage.text }}
                  </div>
                  <div v-if="reservation.paymentMethod === 'CreditCard'" class="final-amount-section mt-3">
                    <p class="final-amount">
                      <strong>{{ $t('reservation.amountToPay') }}:</strong>
                      <span v-if="appliedVoucher" class="price-with-discount">
                        <span class="original-price">€{{ formatPrice(calculatedPrice * prePaymentPercentage / 100) }}</span>
                        <span class="discounted-price">€{{ formatPrice(prePaymentAmount) }}</span>
                      </span>
                      <span v-else>€{{ formatPrice(prePaymentAmount) }}</span>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Step 4: Confirmation -->
              <div v-if="currentStep === 4" class="step-content">
                <h4>{{ $t('reservation.confirmation') }}</h4>
                
                <div class="reservation-summary">
                  <div class="row">
                    <div class="col-md-6">
                      <h5>{{ $t('reservation.experienceDetails') }}</h5>
                      <p><strong>{{ $t('reservation.experience') }}:</strong> {{ experience.name }}</p>
                      <p><strong>{{ $t('reservation.date') }}:</strong> {{ formatDate(selectedSlot.date) }}</p>
                      <p><strong>{{ $t('reservation.time') }}:</strong> {{ selectedSlot.startTime }}</p>
                      <p><strong>{{ $t('reservation.people') }}:</strong> {{ reservation.numberOfPeople }}</p>
                    </div>
                    <div class="col-md-6">
                      <h5>{{ $t('reservation.contactInfo') }}</h5>
                      <p><strong>{{ $t('reservation.name') }}:</strong> {{ reservation.touristName }}</p>
                      <p><strong>{{ $t('reservation.email') }}:</strong> {{ reservation.touristEmail }}</p>
                      <p><strong>{{ $t('reservation.phone') }}:</strong> {{ reservation.touristPhone }}</p>
                      <p><strong>{{ $t('reservation.language') }}:</strong> {{ getLanguageName(reservation.language) }}</p>
                    </div>
                  </div>
                  
                  <div class="people-summary mt-4">
                    <h5>{{ $t('reservation.peopleSummary') }}</h5>
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                          <tr>
                            <th>{{ $t('reservation.personName') }}</th>
                            <th>{{ $t('reservation.ageBracket') }}</th>
                            <th v-if="showPersonPrices">{{ $t('reservation.price') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(person, index) in reservation.people" :key="index">
                            <td>{{ person.name }}</td>
                            <td>{{ getAgeBracketName(person.ageBracket) }}</td>
                            <td v-if="showPersonPrices">{{ personPriceLabel(person) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <div v-if="calculatedPrice > 0" class="price-summary mt-4">
                    <h5>{{ $t('experiences.price') }}</h5>
                    
                    <!-- Pre-payment breakdown -->
                    <div v-if="experience.needsPrePayment" class="payment-breakdown">
                      <div class="breakdown-item">
                        <span>{{ $t('reservation.totalPrice') }}:</span>
                        <strong>€{{ formatPrice(finalPrice) }}</strong>
                      </div>
                      <div v-if="prePaymentPercentage < 100" class="breakdown-item highlight">
                        <span>{{ $t('reservation.payNow') }} ({{ prePaymentPercentage }}%):</span>
                        <strong class="text-primary">€{{ formatPrice(prePaymentAmount) }}</strong>
                      </div>
                      <div v-if="prePaymentPercentage < 100" class="breakdown-item">
                        <span>{{ $t('reservation.payOnArrival') }}:</span>
                        <strong>€{{ formatPrice(remainingAmount) }}</strong>
                      </div>
                      <div v-else class="breakdown-item highlight">
                        <span>{{ $t('reservation.payNow') }}:</span>
                        <strong class="text-primary">€{{ formatPrice(prePaymentAmount) }}</strong>
                      </div>
                    </div>
                    
                    <!-- No pre-payment (fallback) -->
                    <div v-else class="price-display">
                      <span v-if="appliedVoucher" class="price-with-discount">
                        <span class="original-price">€{{ formatPrice(calculatedPrice) }}</span>
                        <strong class="discounted-price">€{{ formatPrice(finalPrice) }}</strong>
                      </span>
                      <strong v-else>€{{ formatPrice(finalPrice) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation Buttons -->
              <div class="form-navigation">
                <div v-if="submitError" class="error-message">{{ submitError }}</div>
                <div class="navigation-buttons">
                  <button 
                    v-if="currentStep > 1" 
                    type="button" 
                    class="btn-nav btn-nav-secondary"
                    @click="previousStep"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M12 4L6 10L12 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>{{ $t('reservation.previous') }}</span>
                  </button>
                  <div v-else class="btn-spacer"></div>
                  
                  <button 
                    v-if="currentStep < 4" 
                    type="button" 
                    class="btn-nav btn-nav-primary"
                    @click="nextStep"
                  >
                    <span>{{ $t('reservation.next') }}</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M8 4L14 10L8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  
                  <button 
                    v-if="currentStep === 4" 
                    type="button" 
                    class="btn-nav btn-nav-primary"
                    @click="submitReservation"
                    :disabled="submitting"
                  >
                    <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
                    <span>{{ $t('reservation.submit') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseDateString } from '@/services/slotAvailability.service.js';
import { PAYMENTS_SERVICE } from '@/services/payments.service.js';

export default {
  name: 'ReservationForm',
  props: {
    experience: {
      type: Object,
      required: true
    },
    selectedSlot: {
      type: Object,
      required: true
    },
    numberOfPeople: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      submitting: false,
      voucherLoading: false,
      voucherCode: '',
      voucherMessage: null,
      appliedVoucher: null,
      submitError: null,
      reservation: {
        touristName: '',
        touristEmail: '',
        touristPhone: '',
        language: '',
        restrictions: '',
        notes: '',
        numberOfPeople: this.numberOfPeople,
        people: [],
        paymentMethod: 'BankTransfer'
      },
      errors: {},
      cardEnabled: true
    }
  },
  computed: {
    // Effective prices of the selected slot (price periods); older APIs send none, so fall back to the experience's base price.
    slotPrice() {
      const price = this.selectedSlot?.price;
      return (price !== undefined && price !== null) ? price : this.experience.price;
    },
    slotAgeBracketPrices() {
      const brackets = this.selectedSlot?.ageBracketPrices;
      return (brackets && Object.keys(brackets).length > 0) ? brackets : this.experience.ageBracketPrices;
    },
    // Per-person experiences priced by age bracket: show what each person costs on the slot's date
    showPersonPrices() {
      return this.experience.priceType === 'PerPerson' &&
             !!this.slotAgeBracketPrices && Object.keys(this.slotAgeBracketPrices).length > 0;
    },
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return this.reservation.touristName && 
                 this.reservation.touristEmail && 
                 this.reservation.touristPhone && 
                 this.reservation.language;
        case 2:
          return this.reservation.people.length === this.reservation.numberOfPeople &&
                 this.reservation.people.every(person => 
                   person.name && person.ageBracket
                 );
        case 3:
          return !this.experience.needsPrePayment || this.reservation.paymentMethod;
        case 4:
          return true;
        default:
          return false;
      }
    },
    calculatedPrice() {
      // If price type is PerExperience, return the fixed price
      if (this.experience.priceType === 'PerExperience') {
        return this.slotPrice || 0;
      }
      
      // If price type is PerPerson, calculate based on age brackets
      if (this.experience.priceType === 'PerPerson') {
        // If age bracket prices are available, use them
        if (this.slotAgeBracketPrices && this.reservation.people && this.reservation.people.length > 0) {
          return this.reservation.people.reduce((total, person) => {
            const bracket = person.ageBracket;
            const bracketPrice = this.slotAgeBracketPrices[bracket] || 0;
            return total + bracketPrice;
          }, 0);
        }
        
        // Fallback: use single price * number of people
        const price = this.slotPrice || this.experience.prePaymentAmount || 0;
        return price * this.reservation.numberOfPeople;
      }
      
      // Default fallback
      return this.slotPrice || 0;
    },
    finalPrice() {
      let price = this.calculatedPrice;
      if (this.appliedVoucher) {
        if (this.appliedVoucher.isPercentage) {
          price = price * (1 - this.appliedVoucher.value / 100);
        } else {
          price = Math.max(0, price - this.appliedVoucher.value);
        }
      }
      return price;
    },
    prePaymentPercentage() {
      return this.experience.prePaymentPercentage || 100;
    },
    prePaymentAmount() {
      const percentage = this.prePaymentPercentage;
      return (this.finalPrice * percentage) / 100;
    },
    remainingAmount() {
      return this.finalPrice - this.prePaymentAmount;
    },
    maxAllowedPeople() {
      return this.selectedSlot?.availableSpots ?? this.selectedSlot?.maxPeople ?? 20;
    }
  },
  mounted() {
    this.initializePeople();
    this.loadPaymentsConfig();
  },
  methods: {
    async loadPaymentsConfig() {
      try {
        const config = await PAYMENTS_SERVICE.getConfig();
        if (config && config.cardEnabled === false) {
          this.cardEnabled = false;
          this.reservation.paymentMethod = 'BankTransfer';
        }
      } catch (error) {
        // Keep both payment options when the config can't be loaded
        console.error('Error loading payments config:', error);
      }
    },
    initializePeople() {
      this.reservation.people = [];
      for (let i = 0; i < this.numberOfPeople; i++) {
        this.reservation.people.push({
          name: '',
          ageBracket: ''
        });
      }
    },
    
    nextStep() {
      if (this.validateCurrentStep()) {
        this.currentStep++;
        this.errors = {};
      }
    },
    
    previousStep() {
      this.currentStep--;
      this.errors = {};
    },
    
    validateCurrentStep() {
      this.errors = {};
      let isValid = true;
      
      if (this.currentStep === 1) {
        if (!this.reservation.touristName) {
          this.errors.touristName = this.$t('validation.required');
          isValid = false;
        }
        if (!this.reservation.touristEmail) {
          this.errors.touristEmail = this.$t('validation.required');
          isValid = false;
        } else if (!this.isValidEmail(this.reservation.touristEmail)) {
          this.errors.touristEmail = this.$t('validation.invalidEmail');
          isValid = false;
        }
        if (!this.reservation.touristPhone) {
          this.errors.touristPhone = this.$t('validation.required');
          isValid = false;
        } else if (!this.isValidPhone(this.reservation.touristPhone)) {
          this.errors.touristPhone = this.$t('validation.invalidPhone') || 'Número de telefone inválido';
          isValid = false;
        }
        if (!this.reservation.language) {
          this.errors.language = this.$t('validation.required');
          isValid = false;
        }
      } else if (this.currentStep === 2) {
        // Validate that we have at least the minimum number of people
        if (this.reservation.people.length < this.reservation.numberOfPeople) {
          isValid = false;
        }
        
        // Validate each person's data
        this.reservation.people.forEach((person, index) => {
          if (!person.name || person.name.trim() === '') {
            this.errors[`person_${index}_name`] = this.$t('validation.required');
            isValid = false;
          }
          if (!person.ageBracket || person.ageBracket.trim() === '') {
            this.errors[`person_${index}_ageBracket`] = this.$t('validation.required');
            isValid = false;
          }
        });
      }
      
      return isValid;
    },
    
    addPerson() {
      if (this.reservation.people.length >= this.maxAllowedPeople) return;
      this.reservation.people.push({
        name: '',
        ageBracket: ''
      });
      this.reservation.numberOfPeople++;
    },
    
    removePerson(index) {
      this.reservation.people.splice(index, 1);
      this.reservation.numberOfPeople--;
    },
    
    async applyVoucher() {
      if (!this.voucherCode) return;
      
      this.voucherLoading = true;
      this.voucherMessage = null;
      this.appliedVoucher = null;
      
      try {
        const { API_HELPER } = await import('@/helpers/api.js');
        const url = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'Vouchers', 'validate', this.voucherCode]);
        const params = { experienceId: this.experience.id };
        const requestOptions = {
          method: 'GET',
          headers: { "Content-Type": "application/json" }
        };
        
        const response = await API_HELPER.apiCall(url, params, requestOptions);
        
        if (response && response.data) {
          this.appliedVoucher = response.data;
        this.voucherMessage = {
          type: 'success',
          text: this.$t('reservation.voucherApplied')
        };
        } else {
          throw new Error('Invalid response');
        }
      } catch (error) {
        console.error('Error validating voucher:', error);
        this.appliedVoucher = null;
        this.voucherMessage = {
          type: 'error',
          text: this.$t('reservation.voucherError')
        };
      } finally {
        this.voucherLoading = false;
      }
    },
    
    async submitReservation() {
      this.submitting = true;
      this.submitError = null;
      
      try {
        // Validate and format the date
        let reservationDate;
        if (this.selectedSlot && this.selectedSlot.date) {
          const dateObj = parseDateString(this.selectedSlot.date);

          if (!dateObj || isNaN(dateObj.getTime())) {
            throw new Error('Data inválida selecionada');
          }
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          reservationDate = `${year}-${month}-${day}`;
        } else {
          throw new Error('Nenhuma data foi selecionada');
        }

        // Calculate final price with voucher discount
        const finalPrice = this.finalPrice;
        const prePaymentAmountValue = this.prePaymentAmount;

        const reservationData = {
          experienceId: this.experience.id,
          experienceAvailabilityId: this.selectedSlot.id,
          touristName: this.reservation.touristName,
          touristEmail: this.reservation.touristEmail,
          touristPhone: this.reservation.touristPhone,
          reservationDate: reservationDate,
          numberOfPeople: this.reservation.numberOfPeople,
          language: this.reservation.language,
          restrictions: this.reservation.restrictions || '',
          notes: this.reservation.notes || '',
          needsPrePayment: this.experience.needsPrePayment || false,
          prePaymentAmount: this.experience.needsPrePayment ? prePaymentAmountValue : 0,
          totalPrice: finalPrice,
          prePaymentPercentage: this.prePaymentPercentage,
          paymentMethod: this.reservation.paymentMethod === 'BankTransfer' ? 0 : 1, // 0 = BankTransfer, 1 = CreditCard
          status: 0, // 0 = Pending, 1 = Confirmed, 2 = Cancelled, 3 = Completed
          adminNotes: '',
          voucherId: this.appliedVoucher ? this.appliedVoucher.id : null,
          discountAmount: this.appliedVoucher ? (this.calculatedPrice - finalPrice) : 0,
          people: this.reservation.people.map(person => ({
            name: person.name,
            ageBracket: person.ageBracket === 'ZeroToFive' ? 0 : 
                        person.ageBracket === 'SixToEleven' ? 1 : 
                        person.ageBracket === 'TwelveToSeventeen' ? 2 : 
                        person.ageBracket === 'Adult' ? 3 : 4 // 0 = ZeroToFive, 1 = SixToEleven, 2 = TwelveToSeventeen, 3 = Adult, 4 = Senior
          }))
        };
        
        // If credit card payment, redirect to payment page
        if (this.reservation.paymentMethod === 'CreditCard' && this.experience.needsPrePayment) {
          // Store reservation data in Vuex store (more secure than sessionStorage)
          this.$store.dispatch('payment/setPendingReservation', reservationData);
          this.$router.push({
            name: 'Payment',
            params: {
              experienceId: this.experience.id
            }
          });
          return;
        }
        
        // Import the service dynamically to avoid circular dependencies
        const { RESERVATIONS_SERVICE } = await import('@/services/reservations.service.js');
        const result = await RESERVATIONS_SERVICE.createReservation(reservationData);
        
        // Show success message
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('reservation.successMessage')
        });
        
        this.$emit('reservation-submitted', result);
        
        // Redirect to experiences page after 2 seconds
        setTimeout(() => {
          this.$router.push({ name: 'Experiences' });
        }, 2000);
        
      } catch (error) {
        console.error('Error submitting reservation:', error);
        this.submitError = typeof error === 'string' ? error : this.$t('reservation.submitError');
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.submitError
        });
      } finally {
        this.submitting = false;
      }
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidPhone(phone) {
      // Accept international format: optional + followed by 7-15 digits (spaces/dashes allowed)
      const cleaned = phone.replace(/[\s\-()]/g, '');
      const phoneRegex = /^\+?\d{7,15}$/;
      return phoneRegex.test(cleaned);
    },
    
    formatDate(date) {
      if (!date) return 'Invalid Date';
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return 'Invalid Date';
        return dateObj.toLocaleDateString(this.$i18n.locale);
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid Date';
      }
    },
    
    formatPrice(price) {
      if (!price) return '0.00';
      return parseFloat(price).toFixed(2);
    },
    
    getLanguageName(lang) {
      return lang === 'pt' ? 'Português' : 'English';
    },
    
    // " — €33.00" after an age-bracket option (" — Grátis" when free); nothing when the bracket is unpriced
    bracketPriceSuffix(bracket) {
      if (!this.showPersonPrices) return '';
      const price = this.slotAgeBracketPrices[bracket];
      if (price === undefined || price === null) return '';
      return price > 0 ? ` — €${this.formatPrice(price)}` : ` — ${this.$t('experiences.free')}`;
    },

    personPriceLabel(person) {
      const price = this.slotAgeBracketPrices[person.ageBracket] || 0;
      return price > 0 ? `€${this.formatPrice(price)}` : this.$t('experiences.free');
    },

    getAgeBracketName(bracket) {
      return this.$t(`reservation.ageBrackets.${bracket.toLowerCase()}`);
    }
  }
}
</script>

<style scoped>
/* Modern Reservation Form Styles */
.reservation-form {
  padding: 2rem 0;
}

.reservation-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.reservation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.reservation-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.reservation-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
  letter-spacing: -0.5px;
}

.reservation-body {
  padding: 0;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  padding: 0 1rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 20px;
  left: calc(50% + 20px);
  right: calc(-50% + 20px);
  height: 3px;
  background: #e5e7eb;
  z-index: 1;
  border-radius: 2px;
  transition: background 0.3s ease;
}

.step.completed:not(:last-child)::after {
  background: var(--primary-color);
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 4px 12px rgba(177, 209, 160, 0.3);
}

.step.completed .step-number {
  background: var(--primary-color);
  color: white;
}

.step-label {
  font-size: 0.875rem;
  text-align: center;
  color: #6b7280;
  font-weight: 500;
  transition: color 0.3s ease;
}

.step.active .step-label,
.step.completed .step-label {
  color: var(--primary-color);
  font-weight: 600;
}

/* Step Content */
.step-content {
  min-height: 400px;
  padding: 1rem 0;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0 0 1rem 0;
  text-align: center;
}

.step-subtitle {
  color: #6b7280;
  font-size: 1rem;
  text-align: center;
  margin: 0 0 2rem 0;
}

/* Modern Form Styles */
.form-row-modern {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group-modern {
  margin-bottom: 1.5rem;
}

.form-group-modern.full-width {
  grid-column: 1 / -1;
}

.form-label-modern {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--secondary-color);
}

.label-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.form-input-modern {
  width: 100%;
  padding: 0.875rem 1.25rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #ffffff;
  color: var(--secondary-color);
  font-family: inherit;
}

.form-input-modern:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px rgba(177, 209, 160, 0.1);
}

.form-input-modern:hover {
  border-color: #d1d5db;
}

.form-input-modern.input-error {
  border-color: #ef4444;
}

.form-input-modern.input-error:focus {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

select.form-input-modern {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 3rem;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
}

/* Navigation Buttons */
.form-navigation {
  border-top: 2px solid #f3f4f6;
  padding-top: 2rem;
  margin-top: 2rem;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.btn-nav {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-nav-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #9fc48a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(177, 209, 160, 0.3);
}

.btn-nav-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(177, 209, 160, 0.4);
  background: linear-gradient(135deg, #9fc48a 0%, var(--primary-color) 100%);
}

.btn-nav-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-nav-secondary {
  background: #ffffff;
  color: var(--secondary-color);
  border: 2px solid #e5e7eb;
}

.btn-nav-secondary:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(177, 209, 160, 0.05);
}

.btn-spacer {
  flex: 1;
}

/* Person Cards */
.person-card .card {
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.person-card .card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(177, 209, 160, 0.1);
}

.person-card .card-header-modern {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  border-radius: 16px 16px 0 0;
  padding: 1rem 1.5rem;
}

.card-title-modern {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin: 0;
}

.btn-remove-person {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid #ef4444;
  border-radius: 8px;
  background: #ffffff;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.btn-remove-person:hover {
  background: #ef4444;
  color: #ffffff;
  transform: scale(1.05);
}

.btn-add-person {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: 2px dashed var(--primary-color);
  border-radius: 12px;
  background: rgba(177, 209, 160, 0.05);
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.btn-add-person:hover {
  background: rgba(177, 209, 160, 0.15);
  border-style: solid;
  transform: translateY(-2px);
}

.person-card .card-body {
  padding: 1.5rem;
}

.person-card .card-body .form-row-modern {
  margin-bottom: 0;
}

/* Payment Breakdown */
.payment-breakdown {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.breakdown-item:last-child {
  border-bottom: none;
}

.breakdown-item.highlight {
  background: rgba(177, 209, 160, 0.1);
  margin: 0.5rem -1.25rem;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border-bottom: none;
}

.breakdown-item .text-primary {
  color: var(--primary-color) !important;
  font-size: 1.1rem;
}

.breakdown-item .price-with-discount {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.breakdown-item .original-price {
  color: #9ca3af;
  font-size: 0.95em;
  font-weight: 500;
  position: relative;
  display: inline-block;
}

.breakdown-item .original-price::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 2px;
  background-color: #9ca3af;
  transform: translateY(-50%);
  z-index: 1;
}

.breakdown-item .discounted-price {
  color: #000000;
  font-weight: 700;
  font-size: 1.1em;
}

/* Payment Options */
.payment-options .form-check-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-options .form-check-input:checked + .form-check-label {
  border-color: var(--primary-color);
  background: rgba(177, 209, 160, 0.05);
}

.bank-info {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.final-amount {
  font-size: 1.1rem;
  color: var(--secondary-color);
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #e5e7eb;
}

.final-amount strong {
  font-weight: 700;
  color: var(--secondary-color);
}

.final-amount-section {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.final-amount-section .final-amount {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  margin-bottom: 0;
}

.price-with-discount {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.original-price {
  position: relative;
  color: #9ca3af;
  font-size: 0.95em;
  font-weight: 500;
  display: inline-block;
}

.original-price::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 2px;
  background-color: #9ca3af;
  transform: translateY(-50%);
  z-index: 1;
}


.discounted-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.1em;
}

.price-display .price-with-discount {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
}

.price-display .original-price {
  font-size: 1.4rem;
  color: #9ca3af;
  position: relative;
  display: inline-block;
  font-weight: 500;
}

.price-display .original-price::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 2px;
  background-color: #9ca3af;
  transform: translateY(-50%);
  z-index: 1;
}

.price-display .discounted-price {
  font-size: 1.75rem;
  color: var(--primary-color);
  background: linear-gradient(135deg, var(--primary-color) 0%, #8fb87a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.reservation-summary {
  background: #f9fafb;
  padding: 2rem;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
}

.price-summary {
  padding-top: 1.5rem;
  border-top: 2px solid #e5e7eb;
  text-align: center;
}

.price-display {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0.5rem 0 0 0;
}

.price-display strong {
  font-size: 1.75rem;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2d3748 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Responsive */
@media (max-width: 768px) {
  .reservation-form {
    padding: 1.5rem 0;
  }
  
  .reservation-card {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
  
  .reservation-header {
    margin-bottom: 2rem;
  }
  
  .reservation-title {
    font-size: 1.75rem;
  }
  
  .progress-steps {
    flex-direction: row;
    gap: 0;
    padding: 0;
    margin-bottom: 2rem;
    justify-content: space-between;
  }
  
  .step {
    flex: 1;
    min-width: 0;
  }
  
  .step:not(:last-child)::after {
    display: block;
    left: calc(50% + 18px);
    right: calc(-50% + 18px);
  }
  
  .step-number {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .step-label {
    font-size: 0.75rem;
    line-height: 1.2;
  }
  
  .step-content {
    min-height: auto;
    padding: 0.5rem 0;
  }
  
  .step-title {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
  
  .step-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
  
  .form-row-modern {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .form-group-modern {
    margin-bottom: 1.25rem;
  }
  
  .form-label-modern {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .form-input-modern {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
  }
  
  .person-card .card-header-modern {
    padding: 0.875rem 1rem;
  }
  
  .person-card .card-body {
    padding: 1rem;
  }
  
  .card-title-modern {
    font-size: 1rem;
  }
  
  .btn-add-person {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
  
  .navigation-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .btn-nav {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
  }
  
  .btn-spacer {
    display: none;
  }
  
  .reservation-summary {
    padding: 1.5rem;
  }
  
  .price-display {
    font-size: 1.25rem;
  }
  
  .price-display strong {
    font-size: 1.5rem;
  }
  
  .price-display .original-price {
    font-size: 1.1rem;
  }
  
  .price-display .discounted-price {
    font-size: 1.35rem;
  }
  
  .bank-info {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .reservation-form {
    padding: 1rem 0;
  }
  
  .reservation-card {
    padding: 1.5rem 1rem;
    border-radius: 16px;
  }
  
  .reservation-header {
    margin-bottom: 1.5rem;
  }
  
  .reservation-title {
    font-size: 1.5rem;
  }
  
  .progress-steps {
    gap: 0;
    margin-bottom: 1.5rem;
  }
  
  .step {
    flex: 1;
    min-width: 0;
  }
  
  .step:not(:last-child)::after {
    left: calc(50% + 16px);
    right: calc(-50% + 16px);
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    font-size: 0.85rem;
  }
  
  .step-label {
    font-size: 0.7rem;
  }
  
  .step-title {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
  
  .step-subtitle {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
  
  .form-group-modern {
    margin-bottom: 1rem;
  }
  
  .form-label-modern {
    font-size: 0.85rem;
  }
  
  .form-input-modern {
    padding: 0.625rem 0.875rem;
    font-size: 0.9rem;
  }
  
  .person-card .card-header-modern {
    padding: 0.75rem;
  }
  
  .person-card .card-body {
    padding: 0.75rem;
  }
  
  .card-title-modern {
    font-size: 0.95rem;
  }
  
  .btn-remove-person {
    width: 28px;
    height: 28px;
  }
  
  .btn-add-person {
    padding: 0.625rem 0.875rem;
    font-size: 0.85rem;
  }
  
  .btn-nav {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }
  
  .reservation-summary {
    padding: 1rem;
  }
  
  .bank-info {
    padding: 0.875rem;
  }
  
  .price-display {
    font-size: 1.1rem;
  }
  
  .price-display strong {
    font-size: 1.35rem;
  }
  
  .price-display .original-price {
    font-size: 0.95rem;
  }
  
  .price-display .discounted-price {
    font-size: 1.2rem;
  }
  
  .payment-options .form-check-label {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
}
</style>

